import type { Collector } from "./types";
import { isBrowser } from "./types";

/**
 * Audio fingerprint collector. Picks variant based on browser context:
 * privacy browsers (Brave, Safari, iOS Chrome) get a more stable
 * configuration that's consistent between normal and private modes.
 */
export const audioCollector: Collector<{ hash?: string; sampleRate?: number }> = {
  name: "audio",
  version: 1,
  timeoutMs: 3000,
  async collect(ctx) {
    const usePrivacyVariant = !!(
      ctx.browser?.isBrave ||
      ctx.browser?.isSafari ||
      ctx.browser?.isIosChrome
    );
    const hash = usePrivacyVariant ? await audioHashBrave() : await audioHash();
    const sampleRate = isBrowser ? readAudioSampleRate() : undefined;
    return { hash, sampleRate };
  },
};

/**
 * Read AudioContext sampleRate, then close the context to release the
 * native audio resource. close() returns a Promise but we don't await —
 * fire-and-forget is fine; even if it rejects (older Safari) we've already
 * read what we needed. Without close() the context lingers until GC,
 * holding a measurable amount of memory + audio-thread state per page load.
 */
export function readAudioSampleRate(): number | undefined {
  try {
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return undefined;
    const ctx = new Ctor();
    const rate = ctx.sampleRate as number;
    Promise.resolve(ctx.close?.()).catch(() => undefined);
    return rate;
  } catch {
    return undefined;
  }
}

async function audioHash(): Promise<string | undefined> {
  if (!isBrowser) return undefined;
  try {
    const AC: any =
      (window as any).OfflineAudioContext || (window as any).webkitOfflineAudioContext;
    if (!AC) return undefined;
    const ctx = new AC(1, 44100, 44100);
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = 1000;
    const comp = ctx.createDynamicsCompressor();
    osc.connect(comp);
    comp.connect(ctx.destination);
    osc.start(0);
    const rendered = await ctx.startRendering();
    const data: Float32Array = rendered.getChannelData(0).slice(0, 2048);
    const ints = Array.from<number>(data as unknown as Iterable<number>).map((v) =>
      Math.round(Number(v) * 1000),
    );
    const str = ints.join(",");
    const h = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
    return Array.from(new Uint8Array(h))
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return undefined;
  }
}

async function audioHashBrave(): Promise<string | undefined> {
  if (!isBrowser) return undefined;
  try {
    const AC: any =
      (window as any).OfflineAudioContext || (window as any).webkitOfflineAudioContext;
    if (!AC) return undefined;
    const ctx = new AC(1, 22050, 22050);
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = 440;
    osc.connect(ctx.destination);
    osc.start(0);
    const rendered = await ctx.startRendering();
    const data: Float32Array = rendered.getChannelData(0).slice(0, 1024);
    const ints = Array.from<number>(data as unknown as Iterable<number>).map((v) =>
      Math.round(Number(v) * 100),
    );
    const str = ints.join(",");
    const h = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
    return Array.from(new Uint8Array(h))
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return undefined;
  }
}
